/**
 * Feature Grid — 4 variants (icon_3col, alt_rows, tabs, image_cards)
 */
import { useState } from 'react';
import type { FeatureGridContent } from '../schemas/feature_grid';
import {
  Body,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';
import { getLucideIcon } from './lucide-icons';

interface FG {
  content: FeatureGridContent;
}

function IconBox({ name }: { name?: string | null }) {
  const Icon = getLucideIcon(name);
  return (
    <div
      style={{
        width: 48,
        height: 48,
        background: 'var(--ls-accent-soft)',
        color: 'var(--ls-accent)',
        borderRadius: 'var(--ls-radius-md)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        fontWeight: 700,
      }}
    >
      {Icon ? <Icon size={24} strokeWidth={2} /> : (name ? name.charAt(0).toUpperCase() : '✦')}
    </div>
  );
}

export function FeatureGridIcon3Col({ content }: FG) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.features.map((f, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <IconBox name={f.icon} />
              <H3 style={{ fontSize: '1.125rem' }}>{f.title}</H3>
              <Body style={{ fontSize: '0.95rem' }}>{f.description}</Body>
              {f.image && (
                <div style={{ marginTop: 'auto', paddingTop: 4 }}>
                  <VisualPlaceholder src={f.image} aspectRatio="16 / 10" label={f.title} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function FeatureGridAltRows({ content }: FG) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        {content.features.slice(0, 4).map((f, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={i} className="ls-split-pair">
              <div className="ls-split-media" style={{ order: reverse ? 2 : 1 }}>
                <VisualPlaceholder src={f.image} aspectRatio="4 / 3" label={f.title} />
              </div>
              <div style={{ order: reverse ? 1 : 2, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <H3>{f.title}</H3>
                <Body>{f.description}</Body>
              </div>
            </div>
          );
        })}
      </div>
    </SectionFrame>
  );
}

export function FeatureGridTabs({ content }: FG) {
  const [active, setActive] = useState(0);
  const f = content.features[active];
  if (!f) return null;
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            borderBottom: '1px solid var(--ls-border)',
            flexWrap: 'wrap',
          }}
        >
          {content.features.map((feat, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              style={{
                padding: '12px 20px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: active === i ? 'var(--ls-accent)' : 'var(--ls-text-muted)',
                fontWeight: active === i ? 600 : 400,
                borderBottom: active === i ? '2px solid var(--ls-accent)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {feat.title}
            </button>
          ))}
        </div>
        <div className="ls-split-pair">
          <div className="ls-split-media">
            <VisualPlaceholder src={f.image} aspectRatio="4 / 3" label={f.title} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <H3>{f.title}</H3>
            <Body>{f.description}</Body>
          </div>
        </div>
      </div>
    </SectionFrame>
  );
}

export function FeatureGridImageCards({ content }: FG) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-3">
          {content.features.map((f, i) => (
            <div
              key={i}
              className="ls-card"
              style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              <VisualPlaceholder src={f.image} aspectRatio="16 / 10" label={f.title} />
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <H3 style={{ fontSize: '1.125rem' }}>{f.title}</H3>
                <Body style={{ fontSize: '0.9rem' }}>{f.description}</Body>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const featureGridVariants = {
  icon_3col: FeatureGridIcon3Col,
  alt_rows: FeatureGridAltRows,
  tabs: FeatureGridTabs,
  image_cards: FeatureGridImageCards,
};
